<template>
  <div
    v-if="layout && isVisible && isActiveSurface"
    class="measurement-ruler"
    :class="{
      'measurement-ruler--compact': layout.compact,
      'measurement-ruler--keyboard-focus': keyboardFocusVisible,
    }"
    data-testid="measurement-ruler"
    :data-ruler-kind="layout.kind"
    role="group"
    aria-roledescription="draggable measurement ruler"
    tabindex="0"
    :aria-label="rulerAriaLabel"
    :style="rulerStyle"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerCancel"
    @lostpointercapture="handleLostPointerCapture"
    @focus="handleFocus"
    @blur="handleBlur"
    @keydown="handleKeydown"
    @click.stop
    @contextmenu.prevent.stop
  >
    <span
      class="measurement-ruler__hit-target"
      aria-hidden="true"
      :style="hitTargetStyle"
    />
    <svg
      v-if="layout.kind === 'rbc'"
      class="measurement-ruler__graphic"
      data-testid="rbc-ruler-svg"
      :width="layout.contentWidth"
      :height="layout.contentHeight"
      :viewBox="`0 0 ${layout.contentWidth} ${layout.contentHeight}`"
      stroke="#000000"
      aria-hidden="true"
    >
      <desc>{{ rulerGraphicDescription }}</desc>
      <ellipse
        data-testid="rbc-outer-circle"
        :cx="layout.rbc.outerCenterX"
        :cy="layout.rbc.outerCenterY"
        :rx="strokeInclusiveRadius(layout.rbc.outerWidth, layout.strokeWidth)"
        :ry="strokeInclusiveRadius(layout.rbc.outerHeight, layout.strokeWidth)"
        class="measurement-ruler__shape"
        fill="none"
        :stroke-width="layout.strokeWidth"
      />
      <ellipse
        data-testid="rbc-inner-circle"
        :cx="layout.rbc.innerCenterX"
        :cy="layout.rbc.innerCenterY"
        :rx="strokeInclusiveRadius(layout.rbc.innerWidth, layout.strokeWidth)"
        :ry="strokeInclusiveRadius(layout.rbc.innerHeight, layout.strokeWidth)"
        class="measurement-ruler__shape"
        fill="none"
        :stroke-width="layout.strokeWidth"
      />
      <line
        data-testid="rbc-center-horizontal"
        v-bind="layout.rbc.centerHorizontal"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <line
        data-testid="rbc-center-vertical"
        v-bind="layout.rbc.centerVertical"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <circle
        data-testid="rbc-center-point"
        :cx="layout.rbc.outerCenterX"
        :cy="layout.rbc.outerCenterY"
        :r="layout.rbc.centerPointRadius"
        class="measurement-ruler__point"
        fill="#000000"
        stroke="#000000"
      />
      <line
        data-testid="rbc-outer-dimension-line"
        v-bind="layout.rbc.outerDimension"
        class="measurement-ruler__shape"
        stroke-linecap="butt"
        :stroke-width="layout.strokeWidth"
      />
      <line
        data-testid="rbc-outer-dimension-start"
        v-bind="layout.rbc.outerDimensionStartTick"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <line
        data-testid="rbc-outer-dimension-end"
        v-bind="layout.rbc.outerDimensionEndTick"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <text
        data-testid="rbc-outer-dimension-label"
        :x="layout.rbc.outerDimensionLabel.x"
        :y="layout.rbc.outerDimensionLabel.y"
        class="measurement-ruler__text"
        :font-size="layout.labelFontSize"
        fill="#000000"
        stroke="#ffffff"
        stroke-width="1"
        paint-order="stroke fill"
        text-anchor="middle"
        dominant-baseline="middle"
      >{{ rbcOuterLabel }}</text>
      <line
        data-testid="rbc-inner-dimension-line"
        v-bind="layout.rbc.innerDimension"
        class="measurement-ruler__shape"
        stroke-linecap="butt"
        :stroke-width="layout.strokeWidth"
      />
      <line
        data-testid="rbc-inner-dimension-start"
        v-bind="layout.rbc.innerDimensionStartTick"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <line
        data-testid="rbc-inner-dimension-end"
        v-bind="layout.rbc.innerDimensionEndTick"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <text
        data-testid="rbc-inner-dimension-label"
        :x="layout.rbc.innerDimensionLabel.x"
        :y="layout.rbc.innerDimensionLabel.y"
        class="measurement-ruler__text"
        :font-size="layout.labelFontSize"
        fill="#000000"
        stroke="#ffffff"
        stroke-width="1"
        paint-order="stroke fill"
        text-anchor="middle"
        dominant-baseline="middle"
        :transform="rotationTransform(layout.rbc.innerDimensionLabel)"
      >{{ rbcInnerLabel }}</text>
    </svg>

    <svg
      v-else
      class="measurement-ruler__graphic"
      data-testid="wbc-lpc-ruler-svg"
      :width="layout.contentWidth"
      :height="layout.contentHeight"
      :viewBox="`0 0 ${layout.contentWidth} ${layout.contentHeight}`"
      stroke="#000000"
      aria-hidden="true"
    >
      <desc>{{ rulerGraphicDescription }}</desc>
      <line
        data-testid="wbc-lpc-horizontal-arm"
        v-bind="layout.wbcLpc.horizontalArm"
        class="measurement-ruler__shape"
        stroke-linecap="butt"
        :stroke-width="layout.strokeWidth"
      />
      <line
        data-testid="wbc-lpc-vertical-arm"
        v-bind="layout.wbcLpc.verticalArm"
        class="measurement-ruler__shape"
        stroke-linecap="butt"
        :stroke-width="layout.strokeWidth"
      />
      <line
        v-for="(tick, index) in layout.wbcLpc.horizontalTicks"
        :key="`horizontal-${index}`"
        :data-testid="`wbc-lpc-horizontal-tick-${index + 1}`"
        v-bind="tick"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <line
        v-for="(tick, index) in layout.wbcLpc.verticalTicks"
        :key="`vertical-${index}`"
        :data-testid="`wbc-lpc-vertical-tick-${index + 1}`"
        v-bind="tick"
        class="measurement-ruler__shape"
        :stroke-width="layout.strokeWidth"
      />
      <text
        data-testid="wbc-lpc-horizontal-dimension-label"
        :x="layout.wbcLpc.horizontalLabel.x"
        :y="layout.wbcLpc.horizontalLabel.y"
        class="measurement-ruler__text"
        :font-size="layout.labelFontSize"
        fill="#000000"
        stroke="#ffffff"
        stroke-width="1"
        paint-order="stroke fill"
        text-anchor="middle"
        dominant-baseline="middle"
      >{{ wbcLpcArmLabel }}</text>
      <text
        data-testid="wbc-lpc-vertical-dimension-label"
        :x="layout.wbcLpc.verticalLabel.x"
        :y="layout.wbcLpc.verticalLabel.y"
        class="measurement-ruler__text"
        :font-size="layout.labelFontSize"
        fill="#000000"
        stroke="#ffffff"
        stroke-width="1"
        paint-order="stroke fill"
        text-anchor="middle"
        dominant-baseline="middle"
        :transform="rotationTransform(layout.wbcLpc.verticalLabel)"
      >{{ wbcLpcArmLabel }}</text>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useMeasurementRulerStore } from "@/store/modules/measurementRuler";
import {
  RBC_INNER_DIAMETER_MICROMETRES,
  RBC_OUTER_DIAMETER_MICROMETRES,
  RULER_FOCUS_RING_EXTENT_PX,
  WBC_LPC_ARM_LENGTH_MICROMETRES,
  WBC_LPC_TICK_INTERVAL_MICROMETRES,
  calculateRulerLayout,
  normalizedPositionFromTopLeft,
  type RulerKind,
  type RulerLayout,
  type RulerText,
} from "@/utils/measurementRuler";

const props = withDefaults(defineProps<{
  kind?: RulerKind;
  taskId: string;
  surfaceId: string;
  imageId: string;
  surfaceActive?: boolean;
  effectiveMppX?: number | null;
  effectiveMppY?: number | null;
  naturalWidth: number;
  naturalHeight: number;
  renderedWidth: number;
  renderedHeight: number;
}>(), {
  kind: "rbc",
  surfaceActive: true,
  effectiveMppX: null,
  effectiveMppY: null,
});
const emit = defineEmits<{ interactionEnd: [] }>();

type DragState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startLeft: number;
  startTop: number;
  captureTarget: HTMLElement;
};

const rulerStore = useMeasurementRulerStore();
const keyboardFocusVisible = ref(false);
let dragState: DragState | null = null;
let registeredSurface: { taskId: string; surfaceId: string } | null = null;
let pointerFocusInProgress = false;

const rbcOuterLabel = `${RBC_OUTER_DIAMETER_MICROMETRES} µm`;
const rbcInnerLabel = `${RBC_INNER_DIAMETER_MICROMETRES} µm`;
const wbcLpcArmLabel = `${WBC_LPC_ARM_LENGTH_MICROMETRES} µm`;

const isVisible = computed(() => rulerStore.isVisible(props.taskId));
const isActiveSurface = computed(() => (
  rulerStore.isActiveSurface(props.taskId, props.surfaceId)
));
const storedPosition = computed(() => rulerStore.position(props.taskId, props.surfaceId));
const layout = computed(() => calculateRulerLayout({
  kind: props.kind,
  effectiveMppX: props.effectiveMppX,
  effectiveMppY: props.effectiveMppY,
  naturalWidth: props.naturalWidth,
  naturalHeight: props.naturalHeight,
  renderedWidth: props.renderedWidth,
  renderedHeight: props.renderedHeight,
  position: storedPosition.value,
}));
const rulerGraphicDescription = computed(() => (
  props.kind === "rbc"
    ? `RBC measurement ruler with concentric ${RBC_OUTER_DIAMETER_MICROMETRES} micrometre outer and ${RBC_INNER_DIAMETER_MICROMETRES} micrometre inner diameter circles, a center mark, and horizontal and vertical dimension lines.`
    : `WBC and LPC L-shaped measurement ruler with ${WBC_LPC_ARM_LENGTH_MICROMETRES} micrometre horizontal and vertical arms and tick marks every ${WBC_LPC_TICK_INTERVAL_MICROMETRES} micrometres.`
));
const rulerAriaLabel = computed(() => {
  const currentLayout = layout.value;
  const position = currentLayout
    ? {
        x: (currentLayout.left + currentLayout.anchorX) / currentLayout.renderedWidth,
        y: (currentLayout.top + currentLayout.anchorY) / currentLayout.renderedHeight,
      }
    : storedPosition.value;
  const dimensions = props.kind === "rbc"
    ? `RBC ruler with ${RBC_OUTER_DIAMETER_MICROMETRES} micrometre outer diameter and ${RBC_INNER_DIAMETER_MICROMETRES} micrometre inner diameter`
    : `WBC and LPC L-shaped ruler with ${WBC_LPC_ARM_LENGTH_MICROMETRES} micrometre horizontal and vertical arms and ${WBC_LPC_TICK_INTERVAL_MICROMETRES} micrometre ticks`;
  return [
    dimensions,
    `on image ${props.imageId}`,
    `at ${Math.round(position.x * 100)} percent horizontal and ${Math.round(position.y * 100)} percent vertical`,
    "Drag or use the arrow keys to move it",
  ].join(". ");
});

const rulerStyle = computed(() => {
  if (!layout.value) return {};
  return {
    left: `${layout.value.left}px`,
    top: `${layout.value.top}px`,
    width: `${layout.value.contentWidth}px`,
    height: `${layout.value.contentHeight}px`,
    "--measurement-ruler-focus-ring-inset": `-${RULER_FOCUS_RING_EXTENT_PX}px`,
  };
});

const hitTargetStyle = computed(() => {
  if (!layout.value) return {};
  return {
    left: `${layout.value.hitBounds.left}px`,
    top: `${layout.value.hitBounds.top}px`,
    width: `${layout.value.hitBounds.width}px`,
    height: `${layout.value.hitBounds.height}px`,
  };
});

function rotationTransform(text: RulerText): string | undefined {
  if (text.rotation === 0) return undefined;
  return `rotate(${text.rotation} ${text.x} ${text.y})`;
}

function strokeInclusiveRadius(diameter: number, strokeWidth: number): number {
  return Math.max(0, (diameter - strokeWidth) / 2);
}

function handlePointerDown(event: PointerEvent) {
  event.stopPropagation();
  event.preventDefault();
  keyboardFocusVisible.value = false;
  if (event.isPrimary === false || (event.button != null && event.button !== 0)) return;
  if (dragState) return;
  const currentLayout = layout.value;
  const target = event.currentTarget as HTMLElement | null;
  if (!currentLayout || !target) return;
  pointerFocusInProgress = true;
  try {
    target.focus({ preventScroll: true });
  } finally {
    pointerFocusInProgress = false;
  }

  dragState = {
    pointerId: event.pointerId,
    startClientX: event.clientX,
    startClientY: event.clientY,
    startLeft: currentLayout.left,
    startTop: currentLayout.top,
    captureTarget: target,
  };
  try {
    target.setPointerCapture?.(event.pointerId);
  } catch {
    // Pointer capture may be unavailable in synthetic/test environments.
  }
}

function handlePointerMove(event: PointerEvent) {
  if (!dragState || event.pointerId !== dragState.pointerId) return;
  event.stopPropagation();
  event.preventDefault();
  updatePosition(
    dragState.startLeft + (event.clientX - dragState.startClientX),
    dragState.startTop + (event.clientY - dragState.startClientY),
  );
}

function handlePointerUp(event: PointerEvent) {
  if (!dragState || event.pointerId !== dragState.pointerId) return;
  event.stopPropagation();
  event.preventDefault();
  releasePointerCapture();
  emit("interactionEnd");
}

function handlePointerCancel(event: PointerEvent) {
  if (!dragState || event.pointerId !== dragState.pointerId) return;
  event.stopPropagation();
  event.preventDefault();
  releasePointerCapture();
  emit("interactionEnd");
}

function handleLostPointerCapture(event: PointerEvent) {
  if (!dragState || event.pointerId !== dragState.pointerId) return;
  dragState = null;
  emit("interactionEnd");
}

function handleKeydown(event: KeyboardEvent) {
  const currentLayout = layout.value;
  if (!currentLayout) return;

  const distance = event.shiftKey ? 12 : 4;
  const movement: Record<string, { x: number; y: number }> = {
    ArrowLeft: { x: -distance, y: 0 },
    ArrowRight: { x: distance, y: 0 },
    ArrowUp: { x: 0, y: -distance },
    ArrowDown: { x: 0, y: distance },
  };
  const delta = movement[event.key];
  if (!delta) return;

  keyboardFocusVisible.value = true;
  event.stopPropagation();
  event.preventDefault();
  updatePosition(currentLayout.left + delta.x, currentLayout.top + delta.y);
}

function handleFocus() {
  if (!pointerFocusInProgress) keyboardFocusVisible.value = true;
}

function handleBlur() {
  keyboardFocusVisible.value = false;
}

function updatePosition(left: number, top: number) {
  const currentLayout: RulerLayout | null = layout.value;
  if (!currentLayout) return;
  rulerStore.setPosition(
    props.taskId,
    props.surfaceId,
    normalizedPositionFromTopLeft(left, top, currentLayout, storedPosition.value),
  );
}

function releasePointerCapture() {
  if (!dragState) return;
  try {
    dragState.captureTarget.releasePointerCapture?.(dragState.pointerId);
  } catch {
    // The browser may have already released capture after cancellation.
  }
  dragState = null;
}

function resetTransientInteractionState() {
  releasePointerCapture();
  keyboardFocusVisible.value = false;
  pointerFocusInProgress = false;
}

function unregisterCurrentSurface() {
  if (!registeredSurface) return;
  rulerStore.unregisterSurface(registeredSurface.taskId, registeredSurface.surfaceId);
  registeredSurface = null;
}

function syncSurfaceRegistration(renderable: boolean, taskId: string, surfaceId: string) {
  if (
    registeredSurface
    && registeredSurface.taskId === taskId
    && registeredSurface.surfaceId === surfaceId
    && renderable
  ) return;

  unregisterCurrentSurface();
  if (!renderable) return;
  rulerStore.registerSurface(taskId, surfaceId);
  registeredSurface = { taskId, surfaceId };
}

watch(
  [layout, () => props.surfaceActive, () => props.taskId, () => props.surfaceId],
  ([currentLayout, surfaceActive, taskId, surfaceId]) => {
    syncSurfaceRegistration(Boolean(currentLayout && surfaceActive), taskId, surfaceId);
    if (!currentLayout || !surfaceActive) {
      resetTransientInteractionState();
    }
  },
  { immediate: true },
);

watch(
  [
    () => props.kind,
    () => props.taskId,
    () => props.surfaceId,
    () => props.effectiveMppX,
    () => props.effectiveMppY,
  ],
  () => releasePointerCapture(),
);

watch(
  [
    () => props.naturalWidth,
    () => props.naturalHeight,
    () => props.renderedWidth,
    () => props.renderedHeight,
  ],
  () => releasePointerCapture(),
);

watch(isVisible, (visible) => {
  if (!visible) resetTransientInteractionState();
});

watch(isActiveSurface, (active) => {
  if (!active) resetTransientInteractionState();
});

onBeforeUnmount(() => {
  resetTransientInteractionState();
  unregisterCurrentSurface();
});
</script>

<style scoped>
.measurement-ruler {
  position: absolute;
  z-index: 4;
  box-sizing: border-box;
  color: #000000;
  cursor: grab;
  pointer-events: none;
  touch-action: none;
  user-select: none;
  overflow: visible;
  outline: none;
  box-shadow: none;
}

.measurement-ruler__hit-target {
  position: absolute;
  z-index: 0;
  background: transparent;
  pointer-events: auto;
}

.measurement-ruler:active {
  cursor: grabbing;
}

.measurement-ruler--keyboard-focus .measurement-ruler__graphic {
  filter:
    drop-shadow(0 0 1px rgba(255, 255, 255, 0.95))
    drop-shadow(0 0 2px rgba(0, 95, 204, 0.9));
  clip-path: inset(var(--measurement-ruler-focus-ring-inset));
}

.measurement-ruler__graphic {
  position: relative;
  z-index: 1;
  display: block;
  overflow: visible;
  pointer-events: none;
}

.measurement-ruler__shape {
  stroke: #000000;
  stroke-linecap: butt;
  stroke-linejoin: miter;
  vector-effect: non-scaling-stroke;
}

.measurement-ruler__point {
  fill: #000000;
  stroke: #000000;
  stroke-width: 1px;
  vector-effect: non-scaling-stroke;
}

.measurement-ruler__text {
  fill: #000000;
  stroke: #ffffff;
  stroke-width: 1px;
  paint-order: stroke fill;
  stroke-linejoin: round;
  font-family: Arial, Helvetica, sans-serif;
  font-weight: 700;
  letter-spacing: 0;
}
</style>
